import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { FiPlus, FiX, FiUser } from 'react-icons/fi';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import { documentsService } from 'services';
import HelpBanner from 'components/HelpBanner';

const DocumentSignContent = ({
    closeModal,
    attachment,
    storeFolderId,
    currentUser,
    currentEmployee = null,
    onSignSuccess
}) => {
    const { t } = useTranslation();
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [deleteOriginal, setDeleteOriginal] = useState(false);
    const [expirationInDays, setExpirationInDays] = useState(30);

    useEffect(() => {
        initializeRecipients();
    }, [currentUser, currentEmployee]);

    const initializeRecipients = () => {
        const initialRecipients = [];

        // Siempre agregar al usuario actual
        if (currentUser) {
            initialRecipients.push({
                email: currentUser.email,
                full_name: `${currentUser.name} ${currentUser.surname}`,
                isCurrentUser: true
            });
        }

        // Si estamos en la ficha de un empleado, agregar al empleado
        if (currentEmployee && currentEmployee.id !== currentUser?.id) {
            initialRecipients.push({
                email: currentEmployee.email,
                full_name: `${currentEmployee.name} ${currentEmployee.surname}`,
                isEmployee: true
            });
        }

        setRecipients(initialRecipients);
    };

    const addRecipient = () => {
        setRecipients([
            ...recipients,
            {
                email: '',
                full_name: '',
                isCustom: true
            }
        ]);
    };

    const removeRecipient = (index) => {
        const newRecipients = recipients.filter((_, i) => i !== index);
        setRecipients(newRecipients);
    };

    const updateRecipient = (index, field, value) => {
        const newRecipients = [...recipients];
        newRecipients[index] = {
            ...newRecipients[index],
            [field]: value
        };
        setRecipients(newRecipients);
    };

    const validateRecipients = () => {
        const newErrors = {};

        if (recipients.length === 0) {
            newErrors.general = 'Debe agregar al menos un firmante';
            return newErrors;
        }

        recipients.forEach((recipient, index) => {
            if (!recipient.email || !recipient.email.trim()) {
                newErrors[`email_${index}`] = 'El email es obligatorio';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient.email)) {
                newErrors[`email_${index}`] = 'El email no es válido';
            }

            if (!recipient.full_name || !recipient.full_name.trim()) {
                newErrors[`name_${index}`] = 'El nombre es obligatorio';
            }
        });

        const days = Number(expirationInDays);
        if (expirationInDays === '' || !Number.isInteger(days) || days < 1) {
            newErrors.expirationInDays = 'Introduce un número entero de días mayor que 0';
        }

        return newErrors;
    };

    const handleSign = async () => {
        const validationErrors = validateRecipients();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            // Preparar los datos para el endpoint
            const signData = {
                recipients: recipients.map(recipient => ({
                    email: recipient.email.trim(),
                    full_name: recipient.full_name.trim()
                })),
                delete_original_document: deleteOriginal,
                expiration_in_days: Number(expirationInDays)
            };

            const response = await documentsService.signAttachment(
                storeFolderId,
                attachment.url,
                signData
            );

            if (!response.ok) {
                const errorMessage = response.message || 'Error al procesar la firma';
                
                console.error('Error en respuesta de Signaturit:', response);
                toastr.error('Error', errorMessage);
                return;
            }

            // Respuesta exitosa
            toastr.success(t('common.success'), t('documents.signSuccess', { name: attachment.name }));
            onSignSuccess && onSignSuccess();
            closeModal();

        } catch (error) {
            console.error('Error al firmar el documento:', error);
            toastr.error('Error', t('documents.errorSign'));
        } finally {
            setLoading(false);
        }
    };

    const getRecipientLabel = (recipient) => {
        if (recipient.isCurrentUser) {
            return 'Usuario actual';
        } else if (recipient.isEmployee) {
            return 'Empleado de la ficha';
        } else {
            return 'Firmante adicional';
        }
    };

    const isEditableRecipient = (recipient) => {
        // Todos los firmantes son editables, incluyendo los predefinidos
        return true;
    };

    return (
        <>
            <div className="modal-body">
                <div className="text-center mb-3">
                    <HelpBanner
                        title="¿Necesitas ayuda?"
                        text="Aprende a firmar documentos con Signaturit paso a paso."
                        linkText="Más información"
                        linkHref="https://help.plain.ninja/es/articles/12089836-firmar-digitalmente-un-documento-con-signaturit"
                    />
                </div>

                <Alert variant="info">
                    <strong>Documento:</strong> {attachment?.name}
                </Alert>

                {errors.general && (
                    <Alert variant="danger">
                        {errors.general}
                    </Alert>
                )}

                <Form noValidate>
                    <h6 className="mb-3">
                        <FiUser className="me-2" />
                        Firmantes del documento:
                    </h6>

                    {recipients.map((recipient, index) => (
                        <div key={index} className="border rounded p-3 mb-3">
                            <Row>
                                <Col md={12}>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="text-muted">
                                            {getRecipientLabel(recipient)}
                                        </small>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => removeRecipient(index)}
                                        >
                                            <FiX />
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="form-group mb-3">
                                        <Form.Label>Nombre completo *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={recipient.full_name}
                                            onChange={(e) => updateRecipient(index, 'full_name', e.target.value)}
                                            isInvalid={!!errors[`name_${index}`]}
                                            disabled={!isEditableRecipient(recipient)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors[`name_${index}`]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="form-group mb-3">
                                        <Form.Label>Email *</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={recipient.email}
                                            onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                                            isInvalid={!!errors[`email_${index}`]}
                                            disabled={!isEditableRecipient(recipient)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors[`email_${index}`]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="outline-primary"
                        onClick={addRecipient}
                        className="mb-3"
                    >
                        <FiPlus className="me-1" />
                        Agregar firmante
                    </Button>

                    <hr className="my-4" />

                    <Form.Group className="form-group mb-4">
                        <Form.Label>Expiración del documento (días)</Form.Label>
                        <Form.Control
                            type="number"
                            step={1}
                            value={expirationInDays}
                            onChange={(e) => setExpirationInDays(e.target.value === '' ? '' : Number(e.target.value))}
                            isInvalid={!!errors.expirationInDays}
                            style={{ maxWidth: '160px' }}
                        />
                        <Form.Control.Feedback type="invalid" style={{ marginBottom: 0 }}>
                            {errors.expirationInDays}
                        </Form.Control.Feedback>
                        {!errors.expirationInDays && (
                            <Form.Text className="text-muted">
                                Pasado este plazo, el documento dejará de estar disponible para firmar. Por defecto, 30 días.
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className="form-group mb-3">
                        <Form.Check
                            type="checkbox"
                            id="deleteOriginal"
                            label="Eliminar documento original al completar las firmas"
                            checked={deleteOriginal}
                            onChange={(e) => setDeleteOriginal(e.target.checked)}
                            className="delete-original-checkbox"
                        />
                        <Form.Text className="text-muted">
                            Si marcas esta opción, solo se conservará la versión firmada del documento en la carpeta.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </div>
            <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={closeModal} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    onClick={handleSign}
                    disabled={loading || recipients.length === 0}
                >
                    {loading ? 'Enviando...' : 'Enviar para firma'}
                </Button>
            </div>
        </>
    );
};

export default DocumentSignContent;
